import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DEFAULT_FILTER_STATE,
  clearedFilterState,
  filterStateToParams,
  parseFilterState,
  type DeckFilterState,
  type DeckSort,
  type DeckStatusFilter,
  type DeckViewMode,
  type UpdatedWindow,
} from './deckFilterState.ts';
import type { TileSize } from '../../components/TileSizeControl.tsx';

/** Where the S/M/L card size has been saved since before any of this existed.
 *  Migrating the preference means READING this key, not renaming it. */
const TILE_SIZE_KEY = 'studio.deckTileSize';

const TILE_SIZES: readonly TileSize[] = ['sm', 'md', 'lg'];

function readSavedTileSize(): TileSize {
  try {
    // usePersistedState wrote it as JSON, so it comes back quoted.
    const raw = localStorage.getItem(TILE_SIZE_KEY);
    if (raw === null) return DEFAULT_FILTER_STATE.tileSize;
    const parsed: unknown = JSON.parse(raw);
    return TILE_SIZES.includes(parsed as TileSize) ? (parsed as TileSize) : DEFAULT_FILTER_STATE.tileSize;
  } catch {
    return DEFAULT_FILTER_STATE.tileSize;
  }
}

function writeSavedTileSize(size: TileSize) {
  try {
    localStorage.setItem(TILE_SIZE_KEY, JSON.stringify(size));
  } catch {
    /* storage unavailable — the choice just doesn't survive a reload */
  }
}

export type DeckFilterActions = {
  /** Change any subset of the axes at once. Every setter below is this. */
  patch: (next: Partial<DeckFilterState>) => void;
  setQuery: (query: string) => void;
  setStatus: (status: DeckStatusFilter) => void;
  /** The tab row's behaviour: clicking the active status clears it. */
  toggleStatus: (status: Exclude<DeckStatusFilter, null>) => void;
  setCategory: (category: string | null) => void;
  setModel: (model: string | null) => void;
  setOwner: (owner: string | null) => void;
  setUpdated: (updated: UpdatedWindow) => void;
  setSort: (sort: DeckSort) => void;
  setView: (view: DeckViewMode) => void;
  setTileSize: (size: TileSize) => void;
  /** Drop every filter, keep sort / view / tile size. */
  clearFilters: () => void;
};

/**
 * The dashboard's view state, mirrored into the URL.
 *
 * One object rather than a handful of `useState`s, because every control the
 * homepage redesign adds has to read the others: the chip row needs to know
 * what is set, "Clear all" needs to unset exactly the filters, and a link has
 * to carry the lot. The URL is written with `replace`, so filtering doesn't
 * fill the back button with every keystroke.
 *
 * Tile size rides along but is NOT part of a shared view: it is a per-browser
 * preference, so its default for URL purposes is whatever this browser saved.
 * A `?size=` in an incoming link still wins, and still doesn't overwrite the
 * saved preference — only the control does that.
 */
export function useDeckFilterState(): { filters: DeckFilterState } & DeckFilterActions {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once: it is a default, not a live input, and re-reading it every
  // render would make the serialisation defaults move under the effect below.
  const tileDefaultRef = useRef<TileSize | null>(null);
  if (tileDefaultRef.current === null) tileDefaultRef.current = readSavedTileSize();
  const defaults = useMemo<DeckFilterState>(
    () => ({ ...DEFAULT_FILTER_STATE, tileSize: tileDefaultRef.current ?? DEFAULT_FILTER_STATE.tileSize }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tileDefaultRef.current],
  );
  // Hydrated straight from the URL rather than in an effect, so the first
  // render already shows the filtered view a shared link asked for.
  const [filters, setFilters] = useState<DeckFilterState>(() => parseFilterState(searchParams, defaults));

  // State is the source of truth from here on; the URL follows it. Compared as
  // strings first because React Router hands back a fresh object each render
  // and setting an identical query string would loop.
  useEffect(() => {
    const next = filterStateToParams(filters, searchParams, defaults);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, defaults]);

  const patch = useCallback((next: Partial<DeckFilterState>) => {
    setFilters((cur) => {
      const merged = { ...cur, ...next };
      // Cheap identity guard: a setter called with the value already held
      // shouldn't re-run every consumer's derivation.
      const same = (Object.keys(merged) as (keyof DeckFilterState)[]).every((k) => merged[k] === cur[k]);
      return same ? cur : merged;
    });
  }, []);

  const setTileSize = useCallback(
    (size: TileSize) => {
      // The control is the only thing that changes the saved preference, so a
      // `?size=` link can be opened without redecorating the recipient's
      // dashboard for good.
      writeSavedTileSize(size);
      tileDefaultRef.current = size;
      patch({ tileSize: size });
    },
    [patch],
  );

  return {
    filters,
    patch,
    setQuery: useCallback((query: string) => patch({ query }), [patch]),
    setStatus: useCallback((status: DeckStatusFilter) => patch({ status }), [patch]),
    toggleStatus: useCallback(
      (status: Exclude<DeckStatusFilter, null>) =>
        setFilters((cur) => ({ ...cur, status: cur.status === status ? null : status })),
      [],
    ),
    setCategory: useCallback((category: string | null) => patch({ category }), [patch]),
    setModel: useCallback((model: string | null) => patch({ model }), [patch]),
    setOwner: useCallback((owner: string | null) => patch({ owner }), [patch]),
    setUpdated: useCallback((updated: UpdatedWindow) => patch({ updated }), [patch]),
    setSort: useCallback((sort: DeckSort) => patch({ sort }), [patch]),
    setView: useCallback((view: DeckViewMode) => patch({ view }), [patch]),
    setTileSize,
    clearFilters: useCallback(() => setFilters((cur) => clearedFilterState(cur)), []),
  };
}
